import type { ReactNode } from "react";
import { Stack, Typography } from "@altimateai/lego";
import { IconList, type Icon } from "@tabler/icons-react";
import HeadingWithIcon from "@/components/icon/HeadingWithIcon";

export interface KeyInfoItem {
  label: string;
  value: ReactNode;
}

interface KeyInfoGridProps {
  title: string;
  /** Tabler icon shown next to the title. Defaults to IconList. */
  icon?: Icon;
  items: KeyInfoItem[];
  /** When true, renders skeleton placeholders instead of items. */
  isLoading?: boolean;
  /** Number of skeleton rows to render while loading. Defaults to 4. */
  skeletonCount?: number;
  /** Overrides the default `aria-label` (which falls back to `title`). */
  ariaLabel?: string;
}

const GRID_CLASS =
  "al-grid al-grid-cols-1 sm:al-grid-cols-2 md:al-grid-cols-3 xl:al-grid-cols-4 al-mt-2";

const KeyInfoGrid = ({
  title,
  icon = IconList,
  items,
  isLoading = false,
  skeletonCount = 4,
  ariaLabel,
}: KeyInfoGridProps) => {
  return (
    <Stack direction="column">
      <HeadingWithIcon icon={icon} title={title} />
      {isLoading ? (
        <dl aria-label={ariaLabel ?? title} aria-busy="true" className={GRID_CLASS}>
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <div key={i} className="al-mb-2">
              {/* Custom skeleton bar (not lego's Skeleton) so our bg class
                  isn't fighting the component's baked-in `al-bg-muted` —
                  tailwind-merge in this repo can't dedupe the `al-` prefix. */}
              <div className="al-animate-pulse al-rounded-md al-bg-gray-15 al-h-4 al-w-40 al-max-w-full" />
            </div>
          ))}
        </dl>
      ) : (
        <dl aria-label={ariaLabel ?? title} className={GRID_CLASS}>
          {items.map(item => (
            <div key={item.label} className="al-mb-2">
              <Stack direction="row" gap="xs" align="center">
                <dt>
                  <Typography variant="body" size="sm" className="al-text-muted-foreground">
                    {item.label}:
                  </Typography>
                </dt>
                <dd className="al-m-0">
                  <Typography variant="body" size="sm" weight="medium">
                    {item.value || "-"}
                  </Typography>
                </dd>
              </Stack>
            </div>
          ))}
        </dl>
      )}
    </Stack>
  );
};

export default KeyInfoGrid;
